import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Sparkles, Library, PlayCircle, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { EDI } from "@/constants/testIds";

const COMMODITY_TINT = {
    "Gold":            "from-amber-500/20  to-slate-950",
    "Copper":          "from-orange-500/20 to-slate-950",
    "PGM":             "from-cyan-500/20   to-slate-950",
    "Coal":            "from-slate-500/25  to-slate-950",
    "Iron Ore":        "from-red-500/20    to-slate-950",
    "Lithium":         "from-emerald-500/20 to-slate-950",
    "Nickel":          "from-sky-500/20    to-slate-950",
    "Cobalt":          "from-indigo-500/20 to-slate-950",
    "Green Hydrogen":  "from-teal-500/25   to-slate-950",
    "Water Treatment": "from-blue-500/20   to-slate-950",
    "Fertiliser":      "from-lime-500/20   to-slate-950",
};

export default function DemoCentre() {
    const [scenarios, setScenarios] = useState([]);
    const nav = useNavigate();

    useEffect(() => {
        api.get("/edi/demo/scenarios")
            .then((r) => setScenarios(r.data.scenarios || []))
            .catch(() => toast.error("Failed to load Enterprise Demonstration Centre"));
    }, []);

    const launch = (key) => nav(`/edi/executive-demo?scenario=${key}`);

    return (
        <div className="space-y-8" data-testid={EDI.demoCentreRoot}>
            <div className="flex items-end justify-between gap-6 flex-wrap">
                <div>
                    <div className="kicker text-[var(--sgsb-gold)]">Enterprise Delivery Intelligence™</div>
                    <h1 className="mt-2 font-display font-black text-white text-4xl md:text-5xl tracking-tighter">
                        Enterprise Demonstration Centre™
                    </h1>
                    <p className="mt-2 text-slate-400 max-w-3xl font-body text-sm">
                        {scenarios.length} guided executive demonstrations — every scenario
                        automatically shows historical knowledge, engineering reuse, automatic
                        baseline generation, procurement and contracting plans, executive
                        dashboard updates, board pack composition, financial savings and
                        knowledge growth.
                    </p>
                </div>
                <div className="flex items-center gap-2 text-[var(--sgsb-gold)] text-xs font-body">
                    <Library className="h-4 w-4" strokeWidth={1.5} />
                    <span className="kicker text-[10px]">One-click executive rehearsal</span>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {scenarios.map((s) => (
                    <button
                        key={s.key}
                        data-testid={EDI.demoCentreCard(s.key)}
                        onClick={() => launch(s.key)}
                        className={`glass hover-lift relative overflow-hidden rounded-xl p-6 text-left transition-transform duration-200 bg-gradient-to-br ${COMMODITY_TINT[s.commodity] || "from-slate-800/40 to-slate-950"}`}
                    >
                        <div className="absolute top-0 left-0 h-[2px] w-14 bg-[var(--sgsb-gold)]" />
                        <div className="relative">
                            <div className="flex items-center justify-between">
                                <div className="kicker text-[var(--sgsb-gold)] text-[10px]">{s.commodity}</div>
                                <ArrowUpRight className="h-3.5 w-3.5 text-[var(--sgsb-gold)]" strokeWidth={2} />
                            </div>
                            <h3 className="mt-2 font-display font-black text-white text-lg tracking-tight leading-snug">
                                {s.title}
                            </h3>
                            <div className="mt-3 flex items-center gap-3 text-xs text-slate-400 font-body flex-wrap">
                                <span className="text-slate-300">{s.process}</span>
                                <span>· {s.capacity}</span>
                                <span>· {s.client}</span>
                            </div>
                            <div className="mt-5 grid grid-cols-3 gap-2">
                                <div>
                                    <div className="kicker text-slate-500 text-[10px]">Similarity</div>
                                    <div className="font-display font-black text-[var(--sgsb-gold)] text-sm">{s.similarity}%</div>
                                </div>
                                <div>
                                    <div className="kicker text-slate-500 text-[10px]">Hours saved</div>
                                    <div className="font-display font-black text-white text-sm">{s.hours_saved.toLocaleString()}</div>
                                </div>
                                <div>
                                    <div className="kicker text-slate-500 text-[10px]">Cost saved</div>
                                    <div className="font-display font-black text-white text-sm">{s.cost_saved}</div>
                                </div>
                            </div>
                            <div className="mt-5 inline-flex items-center gap-2 text-[var(--sgsb-gold)] font-display font-bold text-xs">
                                <PlayCircle className="h-3.5 w-3.5" strokeWidth={2} />
                                Rehearse
                            </div>
                        </div>
                    </button>
                ))}
            </div>
        </div>
    );
}
